import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, type ReactNode } from "react";

import { useTRPC } from "@/lib/trpc";
import { isTrpcErrorCode } from "@/lib/trpc-errors";
import { AuthContext, type AuthContextValue } from "./auth-context";

type AuthProviderProps = {
  children: ReactNode;
};

const isUnauthorizedError = (error: unknown): boolean => isTrpcErrorCode(error, "UNAUTHORIZED");

export function AuthProvider({ children }: AuthProviderProps) {
  const trpc = useTRPC();
  const { data, error, isError, isFetching, refetch } = useQuery(
    trpc.auth.getSession.queryOptions(undefined, {
      // UNAUTHORIZED is a settled anonymous answer — never retry it. A transient network/server
      // failure gets a bounded retry so AuthAnalyticsBridge does not mistake it for a logout.
      retry: (failureCount, retryError) => !isUnauthorizedError(retryError) && failureCount < 2,
      // Disable query during SSR to prevent hydration mismatches
      enabled: typeof window !== "undefined",
    }),
  );
  const session = isError ? undefined : data;
  const refresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const value = useMemo<AuthContextValue>(() => {
    const permissions = session?.permissions ?? [];

    const hasPermission: AuthContextValue["hasPermission"] = (permission) =>
      permissions.some((entry) => entry.permissionId === permission);

    const hasGlobalPermission: AuthContextValue["hasGlobalPermission"] = (permission) =>
      permissions.some(
        (entry) =>
          entry.permissionId === permission &&
          entry.scope.agencyId === null &&
          entry.scope.brandId === null &&
          entry.scope.creatorNetworkId === null,
      );

    // Mirrors the backend helpers in apps/api/src/utils/permission.ts — keep the
    // two in sync. A grant only counts as agency-level when no narrower
    // (brand / creator-network) scope is attached, and so on.
    const hasPermissionForBrand: AuthContextValue["hasPermissionForBrand"] = (permission, brandId, agencyId) => {
      if (!brandId) {
        return false;
      }

      return permissions.some((entry) => {
        if (entry.permissionId !== permission) return false;
        // Compare agencyId directly (no `?? ""` coercion) to mirror the backend
        // hasPermissionForBrand exactly: a null agencyId matches only null-scoped grants.
        const agencyMatches = entry.scope.agencyId === null || entry.scope.agencyId === agencyId;
        const brandMatches = entry.scope.brandId === null || entry.scope.brandId === brandId;
        return agencyMatches && brandMatches && entry.scope.creatorNetworkId === null;
      });
    };

    const hasPermissionForAgency: AuthContextValue["hasPermissionForAgency"] = (permission, agencyId) => {
      if (!agencyId) {
        return false;
      }

      return permissions.some((entry) => {
        if (entry.permissionId !== permission) return false;
        const agencyMatches = entry.scope.agencyId === null || entry.scope.agencyId === agencyId;
        return agencyMatches && entry.scope.brandId === null && entry.scope.creatorNetworkId === null;
      });
    };

    const hasPermissionForCreatorNetwork: AuthContextValue["hasPermissionForCreatorNetwork"] = (
      permission,
      creatorNetworkId,
    ) => {
      if (!creatorNetworkId) {
        return false;
      }

      return permissions.some((entry) => {
        if (entry.permissionId !== permission) return false;
        const networkMatches =
          entry.scope.creatorNetworkId === null || entry.scope.creatorNetworkId === creatorNetworkId;
        return networkMatches && entry.scope.agencyId === null && entry.scope.brandId === null;
      });
    };

    return {
      session,
      isLogged: Boolean(session),
      isFetching,
      isError: isError && !isUnauthorizedError(error),
      refresh,
      hasPermission,
      hasGlobalPermission,
      hasPermissionForBrand,
      hasPermissionForAgency,
      hasPermissionForCreatorNetwork,
    };
  }, [session, isFetching, isError, error, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
